import { t } from '@glory/i18n';
import { type ChangeEvent, type ReactNode, useState } from 'react';

import { type FieldSize, useFieldControl, useFieldState } from './field.tsx';

/** Общие свойства полей с разбором текста: состояние берётся из `Field` или задаётся здесь. */
export interface BoxProps {
  readonly size?: FieldSize | undefined;
  readonly variant?: 'default' | 'cell' | undefined;
  readonly disabled?: boolean | undefined;
  readonly readOnly?: boolean | undefined;
  readonly invalid?: boolean | undefined;
  readonly id?: string | undefined;
  readonly className?: string | undefined;
  readonly placeholder?: string | undefined;
  readonly 'aria-label'?: string | undefined;
  /** Открыт при показе: истории и тесты. */
  readonly defaultOpen?: boolean | undefined;
  /** «Сегодня» для разбора и календаря: истории и тесты. */
  readonly today?: Date | undefined;
}

/**
 * Черновик текста в поле: пока человек печатает — его текст, после ухода с поля — разобранное
 * значение в едином виде. Непонятный текст остаётся в поле с ошибкой, значение не меняется.
 */
export function useDraft<T>({
  value,
  onValueChange,
  parse,
  format: show,
  validate,
  invalidMessage,
}: {
  value: T | null;
  onValueChange: (value: T | null) => void;
  parse: (text: string) => T | null;
  format: (value: T) => string;
  validate?: ((value: T) => string | null) | undefined;
  /** Что сказать про непонятный текст; функция — если причина зависит от текста. */
  invalidMessage: string | ((text: string) => string);
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const trimmed = draft?.trim() ?? '';
  const parsed = draft === null ? value : trimmed === '' ? null : parse(draft);
  const problem =
    draft !== null && trimmed !== '' && parsed === null
      ? typeof invalidMessage === 'function'
        ? invalidMessage(draft)
        : invalidMessage
      : parsed !== null && validate
        ? validate(parsed)
        : null;

  const set = (next: T | null) => {
    setDraft(null);
    onValueChange(next);
  };
  const commit = () => {
    if (draft === null || problem !== null) return;
    set(parsed);
  };

  return {
    text: draft ?? (value === null ? '' : show(value)),
    parsed,
    editing: draft !== null,
    /** Ошибка видна после ухода с поля, пока печатают — подсказка. */
    error: focused ? null : problem,
    problem,
    set,
    commit,
    revert: () => {
      setDraft(null);
    },
    /** Текст в поле напрямую: для полей, которые переформатируют ввод на лету (разряды). */
    setText: setDraft,
    inputProps: {
      value: draft ?? (value === null ? '' : show(value)),
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        setDraft(e.target.value);
      },
      onFocus: () => {
        setFocused(true);
      },
      onBlur: () => {
        setFocused(false);
        commit();
      },
    },
  };
}

/** Состояние элемента ввода: из `Field` вокруг или из свойств. */
export function useBoxState(props: BoxProps) {
  const field = useFieldState();
  const { disabled: fieldDisabled, readOnly: fieldReadOnly, ...control } = useFieldControl({ id: props.id });
  const disabled = props.disabled ?? fieldDisabled ?? false;
  const readOnly = props.readOnly ?? fieldReadOnly ?? false;
  return {
    control,
    disabled,
    readOnly,
    invalid: props.invalid ?? field?.invalid ?? false,
    locked: disabled || readOnly,
  };
}

/** Подсказка под полем: пока печатают — как поняли ввод, иначе — пример форматов. */
export function draftDescription(
  editing: boolean,
  preview: string | null,
  hint: string,
  description: ReactNode,
): ReactNode {
  if (!editing) return description;
  return preview === null ? hint : t('fields.understood', { value: preview });
}
